import _get from 'lodash/get';
import { Link } from 'react-router-dom';
import { History, Location } from 'history';
import { PMCConfig } from '@kyruus/types';
import {
  ExtendedProvider,
  FullAppointmentInfo,
  Log,
  SearchSummary
} from 'Src/types';
import { useAppDrawer } from 'Src/app-drawer';
import { getRelativeParameterizedBookingUrl } from 'Src/utils/getRelativeParameterizedBookingUrl';
import { ListItemButtonProps } from '../../CtaList/CtaListItems';
import { DefaultCtaListItem, trackListItemClick } from '..';
interface UseBookOnlineButtonProps {
  provider: ExtendedProvider;
  config: PMCConfig;
  apptOptions?: FullAppointmentInfo;
  searchSummary?: SearchSummary;
  location: Location;
  primaryText: string;
  secondaryText?: string;
  history: History;
  log: Log;
}

/**
 * Returns props for a "Book Online" button, handling internal/external booking URLs and drawer logic.
 * - Returns undefined if no booking URL is available.
 * - For external URLs:
 *   - returns anchor props.
 * - For internal URLs:
 *   - returns button props to launch Schedule in a drawer if feature is enabled.
 *   - Otherwise returns Link props to navigate to the Schedule flow.
 * @param props - See UseBookOnlineButtonProps
 * @returns ListItemButtonProps for the Book Online CTA, or undefined if not available.
 */
export function useBookOnlineButton({
  provider,
  config,
  apptOptions,
  searchSummary,
  location,
  history,
  log,
  primaryText,
  secondaryText
}: UseBookOnlineButtonProps): ListItemButtonProps | undefined {
  const { closeAppDrawer } = useAppDrawer();
  const bookingUrl = getRelativeParameterizedBookingUrl({
    provider,
    config,
    location,
    apptOptions,
    searchSummary
  });
  if (!bookingUrl) {
    return;
  }
  // External URLs are unchanged by getRelativeParameterizedBookingUrl
  const isExternalBookOnlineUrl = provider.book_online_url === bookingUrl;
  const isScheduleDrawerEnabled = _get(
    config,
    'feature_flags.dbw_render_in_drawer'
  );

  let buttonProps: ListItemButtonProps | undefined;
  const handleClick = () => {
    trackListItemClick({
      log,
      listItem: DefaultCtaListItem.BookOnline,
      providerId: provider.id,
      primaryText,
      secondaryText
    });
  };
  if (isExternalBookOnlineUrl) {
    buttonProps = {
      component: 'a',
      role: 'link',
      href: bookingUrl,
      target: '_blank',
      rel: 'noopener noreferrer',
      onClick: handleClick
    };
  } else if (isScheduleDrawerEnabled) {
    const handleScheduleDrawerClick = () => {
      handleClick();
      history.push(bookingUrl, { provider: { location, provider } });
      closeAppDrawer();
    };
    buttonProps = {
      onClick: handleScheduleDrawerClick,
      role: 'button'
    };
  } else {
    buttonProps = {
      to: bookingUrl,
      component: Link,
      role: 'link',
      onClick: handleClick
    };
  }

  return buttonProps;
}
